import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClientSettings } from '../../contexts/ClientSettingsContext';
import WebRTCDialer from '../../components/dialer/WebRTCDialer';
import { analyticsAPI, contactsAPI } from '../../services/api';
import {
  Phone, ArrowLeft, Clock, Users, Star, StarOff,
  Search, Plus, Trash2, Edit2, PhoneIncoming, PhoneOutgoing,
  PhoneMissed, AlertTriangle, X, Check, Sparkles,
  MessageSquare, MoreVertical, User, Building, Mail
} from 'lucide-react';

// Format phone number for display
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Format duration
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Background color for call direction icon container
const getCallIconBg = (direction, status) => {
  if (direction !== 'inbound') return 'bg-blue-500/20';
  return status === 'missed' ? 'bg-red-500/20' : 'bg-green-500/20';
};

// Pick the right call direction icon
const renderCallIcon = (direction, status) => {
  if (direction === 'inbound') {
    return status === 'missed'
      ? <PhoneMissed className="w-4 h-4 text-red-400" />
      : <PhoneIncoming className="w-4 h-4 text-green-400" />;
  }
  return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
};

const DialerPortal = () => {
  const navigate = useNavigate();
  const { clientUser } = useAuth();
  const { hasFeature, loading: settingsLoading, clientId } = useClientSettings();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('dialer'); // dialer, history, contacts, favorites
  
  // Call history state
  const [callHistory, setCallHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '', company: '' });
  const [contactSearch, setContactSearch] = useState('');
  
  // Favorites state
  const [favorites, setFavorites] = useState([]);
  
  // Dial number from other tabs
  const [dialNumber, setDialNumber] = useState('');
  
  // AI Assist state
  const [showAIPanel, setShowAIPanel] = useState(true);
  
  // Check permissions
  const canMakeCalls = hasFeature('outbound_calls');
  const canReceiveCalls = hasFeature('inbound_calls');
  const isDialerEnabled = canMakeCalls || canReceiveCalls;

  // Fetch call history
  const fetchCallHistory = async () => {
    if (!clientId) return;
    setHistoryLoading(true);
    try {
      const response = await analyticsAPI.getRecentCalls(clientId, { limit: 50 });
      if (response.data.calls) {
        setCallHistory(response.data.calls);
      }
    } catch (error) {
      console.error('Error fetching call history:', error);
      // Fallback to mock data if API fails
      setCallHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch contacts
  const fetchContacts = async () => {
    if (!clientId) return;
    setContactsLoading(true);
    try {
      const response = await contactsAPI.getAll(clientId);
      if (response.data.success) {
        setContacts(response.data.contacts || []);
        setFavorites(response.data.contacts?.filter(c => c.is_favorite) || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Initialize empty if API doesn't exist yet
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    (async () => {
      await fetchCallHistory();
      await fetchContacts();
    })();
  }, [clientId]);

  // Add contact
  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) return;
    
    try {
      const response = await contactsAPI.create(clientId, newContact);
      if (response.data.success) {
        setContacts([response.data.contact, ...contacts]);
        setNewContact({ name: '', phone: '', email: '', company: '' });
        setShowAddContact(false);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      // Add locally if API fails
      const localContact = {
        id: Date.now().toString(),
        ...newContact,
        created_at: new Date().toISOString()
      };
      setContacts([localContact, ...contacts]);
      setNewContact({ name: '', phone: '', email: '', company: '' });
      setShowAddContact(false);
    }
  };

  // Toggle favorite
  const toggleFavorite = async (contact) => {
    const isFav = favorites.some(f => f.id === contact.id);
    
    try {
      await contactsAPI.update(clientId, contact.id, { is_favorite: !isFav });
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
    
    // Update local state
    if (isFav) {
      setFavorites(favorites.filter(f => f.id !== contact.id));
    } else {
      setFavorites([...favorites, { ...contact, is_favorite: true }]);
    }
    
    setContacts(contacts.map(c => 
      c.id === contact.id ? { ...c, is_favorite: !isFav } : c
    ));
  };

  // Delete contact
  const handleDeleteContact = async (contactId) => {
    try {
      await contactsAPI.delete(clientId, contactId);
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
    
    setContacts(contacts.filter(c => c.id !== contactId));
    setFavorites(favorites.filter(f => f.id !== contactId));
  };

  // Dial a number (switch to dialer tab and set number)
  const dialContact = (phone) => {
    setDialNumber(phone);
    setActiveTab('dialer');
  };

  // Filter contacts by search
  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.phone?.includes(contactSearch) ||
    c.company?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isDialerEnabled) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-8 text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Dialer Disabled</h1>
          <p className="text-gray-400 mb-6">
            The dialer feature is not enabled for your account. Please contact your administrator.
          </p>
          <button
            onClick={() => navigate('/client/dashboard')}
            className="px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col" data-testid="dialer-portal">
      {/* Header */}
      <header className="bg-[#0d1117] border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/client/dashboard')}
              className="p-2 hover:bg-gray-800 rounded-lg transition"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div className="flex items-center gap-2">
              <Phone className="w-6 h-6 text-teal-400" />
              <h1 className="text-xl font-bold text-white">Dialer Portal</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
                showAIPanel 
                  ? 'bg-purple-500/20 text-purple-400' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              AI Assist
            </button>
            <div className="text-gray-400 text-sm">
              {clientUser?.name || clientUser?.email}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Left Panel - Tabs */}
        <div className="w-80 border-r border-gray-800 flex flex-col">
          {/* Tab Buttons */}
          <div className="flex border-b border-gray-800">
            {[
              { id: 'dialer', icon: Phone, label: 'Dial' },
              { id: 'history', icon: Clock, label: 'History' },
              { id: 'contacts', icon: Users, label: 'Contacts' },
              { id: 'favorites', icon: Star, label: 'Favorites' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs transition ${
                  activeTab === tab.id
                    ? 'text-teal-400 border-b-2 border-teal-400 bg-teal-400/5'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="p-2">
                {historyLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : callHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No recent calls</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {callHistory.map((call, idx) => (
                      <div
                        key={call.id || idx}
                        className="p-3 hover:bg-gray-800/50 rounded-lg cursor-pointer group"
                        onClick={() => dialContact(call.phone_number || call.to_number)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${getCallIconBg(call.direction, call.status)}`}>
                            {renderCallIcon(call.direction, call.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">
                              {formatPhoneDisplay(call.phone_number || call.to_number)}
                            </p>
                            <p className="text-gray-500 text-xs">
                              {formatRelativeTime(call.created_at)} • {formatDuration(call.duration_seconds)}
                            </p>
                          </div>
                          <Phone className="w-4 h-4 text-teal-400 opacity-0 group-hover:opacity-100 transition" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="p-2">
                {/* Search and Add */}
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Search contacts..."
                      className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
                    />
                  </div>
                  <button
                    onClick={() => setShowAddContact(true)}
                    className="p-2 bg-teal-500 hover:bg-teal-600 rounded-lg transition"
                    data-testid="add-contact-btn"
                  >
                    <Plus className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Add Contact Form */}
                {showAddContact && (
                  <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white text-sm font-medium">New Contact</span>
                      <button onClick={() => setShowAddContact(false)} className="text-gray-500 hover:text-gray-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        placeholder="Name *"
                        className="w-full bg-[#0d1117] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
                      />
                      <input
                        type="tel"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="Phone *"
                        className="w-full bg-[#0d1117] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
                      />
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="Email"
                        className="w-full bg-[#0d1117] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
                      />
                      <input
                        type="text"
                        value={newContact.company}
                        onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                        placeholder="Company"
                        className="w-full bg-[#0d1117] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
                      />
                      <button
                        onClick={handleAddContact}
                        disabled={!newContact.name || !newContact.phone}
                        className="w-full py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded text-white text-sm font-medium transition"
                      >
                        Save Contact
                      </button>
                    </div>
                  </div>
                )}

                {/* Contact List */}
                {contactsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>{contacts.length === 0 ? 'No contacts yet' : 'No matches found'}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="p-3 hover:bg-gray-800/50 rounded-lg group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center">
                            <span className="text-white font-medium">
                              {contact.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{contact.name}</p>
                            <p className="text-gray-500 text-xs truncate">
                              {formatPhoneDisplay(contact.phone)}
                              {contact.company && ` • ${contact.company}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button
                              onClick={() => toggleFavorite(contact)}
                              className="p-1.5 hover:bg-gray-700 rounded"
                            >
                              {favorites.some(f => f.id === contact.id) ? (
                                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              ) : (
                                <StarOff className="w-4 h-4 text-gray-500" />
                              )}
                            </button>
                            <button
                              onClick={() => dialContact(contact.phone)}
                              className="p-1.5 hover:bg-gray-700 rounded"
                            >
                              <Phone className="w-4 h-4 text-teal-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteContact(contact.id)}
                              className="p-1.5 hover:bg-gray-700 rounded"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className="p-2">
                {favorites.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No favorites yet</p>
                    <p className="text-xs mt-1">Star contacts to add them here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {favorites.map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => dialContact(contact.phone)}
                        className="p-3 bg-[#1a1a1f] hover:bg-[#252530] border border-gray-700 rounded-lg text-center transition group"
                        data-testid={`favorite-${contact.id}`}
                      >
                        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mb-2">
                          <span className="text-white text-lg font-medium">
                            {contact.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <p className="text-white text-sm font-medium truncate">{contact.name}</p>
                        <p className="text-gray-500 text-xs truncate">{formatPhoneDisplay(contact.phone)}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Dialer Tab - Quick dial options */}
            {activeTab === 'dialer' && (
              <div className="p-4">
                <p className="text-gray-500 text-sm mb-4">Quick dial from favorites:</p>
                {favorites.length === 0 ? (
                  <p className="text-gray-600 text-xs">No favorites. Add contacts and star them for quick access.</p>
                ) : (
                  <div className="space-y-2">
                    {favorites.slice(0, 5).map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => dialContact(contact.phone)}
                        className="w-full flex items-center gap-3 p-2 hover:bg-gray-800/50 rounded-lg transition"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {contact.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white text-sm">{contact.name}</p>
                          <p className="text-gray-500 text-xs">{formatPhoneDisplay(contact.phone)}</p>
                        </div>
                        <Phone className="w-4 h-4 text-teal-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Center Panel - Dialer */}
        <div className="flex-1 flex items-center justify-center p-8">
          <WebRTCDialer
            userId={clientUser?.id}
            userType="client"
            showAIAssist={showAIPanel}
            initialNumber={dialNumber}
            onCallStart={(call) => {
              console.log('Call started:', call);
              // Refresh history after call
            }}
            onCallEnd={(data) => {
              console.log('Call ended:', data);
              // Refresh history
              setTimeout(fetchCallHistory, 2000);
            }}
          />
        </div>

        {/* Right Panel - AI Assist (when enabled) */}
        {showAIPanel && (
          <div className="w-80 border-l border-gray-800 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h2 className="text-white font-semibold">AI Assistant</h2>
            </div>
            
            <div className="space-y-4">
              {/* Real-time Transcription */}
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400 text-sm">Live Transcription</span>
                </div>
                <div className="h-32 overflow-y-auto text-sm text-gray-300">
                  <p className="text-gray-500 italic">Transcription will appear here during calls...</p>
                </div>
              </div>

              {/* AI Suggestions */}
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-400 text-sm font-medium">Smart Suggestions</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2 text-gray-400">
                    <span className="text-purple-400">→</span>
                    <span>Start calls will generate contextual suggestions</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-400">
                    <span className="text-purple-400">→</span>
                    <span>AI will analyze sentiment in real-time</span>
                  </div>
                </div>
              </div>

              {/* Call Notes */}
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Edit2 className="w-4 h-4 text-teal-400" />
                  <span className="text-gray-400 text-sm">Call Notes</span>
                </div>
                <textarea
                  placeholder="Add notes during your call..."
                  className="w-full bg-transparent border-none text-sm text-white placeholder-gray-600 outline-none resize-none h-20"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DialerPortal;
